import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronRight, Coins, Search, ShieldCheck, Users } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/AppShell';
import { Page, Rise } from '@/components/motion';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { EmptyState, ErrorState, Pager } from '@/components/ui/misc';
import { adminApi } from '@/lib/api/endpoints';
import { initials, timeAgo } from '@/lib/utils';
import { ListSkeleton } from '../ChecksPage';

const LIMIT = 25;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const toBool = (v: string): boolean | undefined => (v === '' ? undefined : v === 'true');

export function AdminUsersPage() {
  const [search, setSearch] = React.useState('');
  const [role, setRole] = React.useState('');
  const [active, setActive] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const debounced = useDebounced(search.trim(), 300);
  const params = { search: debounced || undefined, is_admin: toBool(role), is_active: toBool(active), limit: LIMIT, offset };
  const q = useQuery({ queryKey: ['admin', 'users', params], queryFn: () => adminApi.users(params), placeholderData: keepPreviousData });

  React.useEffect(() => setOffset(0), [debounced, role, active]);

  return (
    <Page>
      <PageHeader eyebrow="Admin" title="Users" description="Search customers, edit accounts and adjust balances." />
      <Rise className="mb-4 flex flex-wrap gap-3">
        <div className="min-w-[220px] flex-1 sm:max-w-sm">
          <Input icon={<Search />} placeholder="Username, name or phone…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={role} onChange={(e) => setRole(e.target.value)} className="w-40">
          <option value="">All roles</option>
          <option value="true">Admins</option>
          <option value="false">Customers</option>
        </Select>
        <Select value={active} onChange={(e) => setActive(e.target.value)} className="w-40">
          <option value="">Any status</option>
          <option value="true">Active</option>
          <option value="false">Disabled</option>
        </Select>
      </Rise>
      <Rise>
        <Card className="overflow-hidden">
          {q.isPending ? (
            <ListSkeleton />
          ) : q.isError ? (
            <ErrorState error={q.error} onRetry={() => q.refetch()} />
          ) : q.data.length === 0 ? (
            <EmptyState icon={<Users />} title="No users found" />
          ) : (
            <div className="divide-y">
              {q.data.map((u) => (
                <Link key={u.id} to={`/admin/users/${u.id}`} className="group flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-secondary/50 sm:px-5">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-sun to-brand-navy text-xs font-bold text-white">
                    {initials(u.full_name || u.username)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 truncate font-semibold">
                      {u.full_name || u.username}
                      {u.is_admin && (
                        <Badge tone="info" className="gap-1">
                          <ShieldCheck className="size-3" /> Admin
                        </Badge>
                      )}
                      {!u.is_active && <Badge tone="danger">Disabled</Badge>}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      @{u.username}
                      {u.phone ? ` · ${u.phone}` : ''} · joined {timeAgo(u.created_at)}
                    </p>
                  </div>
                  <span className="flex items-center gap-1 text-sm font-bold">
                    <Coins className="size-4 text-amber-500" /> {u.coin_balance}
                  </span>
                  <ChevronRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          )}
          <Pager offset={offset} limit={LIMIT} count={q.data?.length ?? 0} onChange={setOffset} loading={q.isFetching} />
        </Card>
      </Rise>
    </Page>
  );
}
